import { EXERCISE_OPTIONS } from '../constants/exerciseOptions';

/** Builds the shared, ready-to-render list of exercise option toggles.
 *
 * Called once by DraggableContent - which owns the seven booleans, `iconOb`
 * and the Redux backup callback - and handed to every surface that toggles
 * them: the three-dot menu, the Options dropdown and the pills. One
 * implementation of `toggle` is what keeps those surfaces from drifting apart.
 *
 * Nothing here persists to the API directly. Each table already runs an effect
 * keyed on these booleans that fires the debounced PUT, so flipping the state
 * is enough.
 *
 * `isItemLocked` is the item-wide soft lock (a save or a parent-item swap in
 * flight). The row's overlay can't reach the menus - both portal to the body -
 * so every toggle checks it here instead.
 *
 * Returns one entry per option, in EXERCISE_OPTIONS order:
 *   { ...descriptor, isChecked, isAvailable, isGreyedOut, isLocked, isBlocked,
 *     isItemLocked, canToggle, toggle }
 *
 * Deliberately not memoized: `onOptionsChange` (saveDropdownStateToRedux)
 * closes over all seven booleans, and the toggle closures must read the
 * current render's values. Caching them would reintroduce exactly the stale
 * closure the `...overrides` escape hatch in saveDropdownStateToRedux exists to
 * work around. This matches the three-dot menu, which rebuilds its item array
 * every render for the same reason.
 */
const useExerciseOptionToggles = ({
  content,
  contentType,
  defaultExOptions,
  iconOb,
  setIconOb,
  values,
  setters,
  onOptionsChange,
  isItemLocked = false,
}) => EXERCISE_OPTIONS.map((option) => {
  const isAvailable = option.isAvailable(content, contentType, defaultExOptions);
  const isGreyedOut = option.isGreyedOut(content, contentType, defaultExOptions);
  const isLocked = option.isLocked(content, contentType, defaultExOptions);
  const isChecked = !!values[option.id];

  /** Resolved here rather than by a descriptor predicate because it depends on
   * the other options' current values, which those predicates never receive.
   *
   * The `!values[option.id]` guard means an option that is itself on is never
   * blocked - otherwise an item that arrived from the server with both flags
   * set would render both rows disabled and trap the coach with no way out. */
  const isBlocked = option.disabledByIds.some(
    (id) => !!values[id] && !values[option.id],
  );

  const toggle = () => {
    if (isGreyedOut || isLocked || isBlocked || isItemLocked) return;

    const newValue = !values[option.id];
    /** Shallow copy on purpose: the nested toggle objects are shared with the
     * inline icon strip, which reads them by reference. A new top-level
     * reference is all React needs to re-render. */
    const newIconOb = { ...iconOb };
    newIconOb[option.iconObKey].enabled = newValue;

    /** Force PR and Disable PR are mutually exclusive - turning one on clears
     * the other, and both values ship in a single change so the Redux backup
     * never holds an intermediate both-on state. */
    if (option.exclusiveWithId) {
      const partner = EXERCISE_OPTIONS.find((o) => o.id === option.exclusiveWithId);
      const partnerValue = newValue ? false : values[option.exclusiveWithId];
      newIconOb[partner.iconObKey].enabled = partnerValue;

      setIconOb(newIconOb);
      setters[option.id](newValue);
      setters[option.exclusiveWithId](partnerValue);
      if (onOptionsChange) {
        onOptionsChange({ [option.id]: newValue, [option.exclusiveWithId]: partnerValue });
      }
      return;
    }

    setIconOb(newIconOb);
    setters[option.id](newValue);
    if (onOptionsChange) {
      onOptionsChange({ [option.id]: newValue });
    }
  };

  return {
    ...option,
    isChecked,
    isAvailable,
    isGreyedOut,
    isLocked,
    isBlocked,
    isItemLocked,
    canToggle: isAvailable && !isGreyedOut && !isLocked && !isBlocked && !isItemLocked,
    toggle,
  };
});

export default useExerciseOptionToggles;
